import { useMemo } from "react";
import { trpc } from "../trpc";
import { FolderTreeItem } from "./useQueryFolderTree";
import { findFolderInTree } from "./utils/findFolderInTree";
import {
  FOLDERS_PAGE_SIZE,
  VirtualFolders,
  isVirtualFolder,
} from "@zapier/utils";
import { getOwnerIdsFromTree } from "./utils/getOwnerIdsFromTree";
import { useMembers } from "../member/useMembers";
import { mapOwnersToFolders } from "./utils/mapOwnersToFolders";
import { TEMP_FOLDER_ID } from "@zapier/utils";
interface Input {
  folderId?: string | VirtualFolders;
  includeChildren?: boolean;
  page?: number | null;
  enabled?: boolean;
}

export function useFolderTreeFolder({
  folderId,
  includeChildren,
  page,
  enabled = true,
}: Input) {
  const { data, isLoading, isError, error, status } =
    trpc.folder.folderTree.useQuery(
      {},
      {
        // Use this select method to pick off a single part of the cached response
        select: (response) => {
          // if we don't need children then we can do a quick lookup of the flat hash
          if (folderId && !includeChildren) {
            const folderInHash = response?.hash[folderId];
            // This is a temporary folder that has been created but not yet saved to the database
            if (!folderInHash) {
              // Temporary folder ids are always 1
              return response.hash[TEMP_FOLDER_ID];
            }

            return folderInHash;
          } else if (folderId) {
            // This will crawl the folder tree for the folder rather than accessing the faster flat hash.
            // This is necessary to return folder children and retain the tree structure in the response.
            const folderInTree = findFolderInTree(response.tree, folderId) as
              | FolderTreeItem
              | undefined;

            // If the folder is not found in the tree, we need to check if it's a temporary folder (folder that is just created)
            if (!folderInTree) {
              return findFolderInTree(response.tree, TEMP_FOLDER_ID) as
                | FolderTreeItem
                | undefined;
            }

            return folderInTree;
          }
          return;
        },
        enabled: !!folderId && enabled,
        // only trigger re-renders when data changes (NOT when loading/fetching changes)
        notifyOnChangeProps: ["data"],
      },
    );

  const folder = data;

  // Get unique ownerIds recursively from the folders tree
  const ownerIds = useMemo(() => {
    if (!folder) {
      return [];
    }
    return getOwnerIdsFromTree([folder as FolderTreeItem]);
  }, [folder]);

  // Fetch all members referenced in the folders tree
  const { members, isPending: isMembersPending = false } = useMembers({
    ids: ownerIds || [],
  });
  // Map owners to folders
  const hydratedFolder = useMemo(() => {
    if (!folder) {
      return;
    }
    return mapOwnersToFolders([folder as FolderTreeItem], members, {
      isMembersPending,
    })[0];
  }, [folder, members, isMembersPending]);

  // Pagination for subfolders/folder children
  const childCount = hydratedFolder?.children?.length || 0;
  const totalSubfolderPages = childCount
    ? Math.ceil(childCount / FOLDERS_PAGE_SIZE)
    : 1;

  const paginatedSubfolders = childCount
    ? (hydratedFolder?.children?.slice(
        (page ? page - 1 : 0) * FOLDERS_PAGE_SIZE,
        (page || 1) * FOLDERS_PAGE_SIZE,
      ) as FolderTreeItem[])
    : ([] as FolderTreeItem[]);

  let folderNotFoundError;

  // If folder is not found in folderTree AND
  // it is not a virtual folder then throw an error (it doesn't exist)
  // Virtual folders are not stored in the folder tree (eg. personal/home, trash)
  if (!isVirtualFolder(folderId)) {
    folderNotFoundError =
      !folder && status === "success" && !isLoading ? 401 : undefined;
  }

  return {
    errorStatus: error?.data?.httpStatus || folderNotFoundError,
    folder: hydratedFolder,
    isError,
    isLoading,
    paginatedSubfolders,
    totalSubfolderPages,
  };
}
